import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getAuditLogs } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { RefreshCw, Activity, Filter } from 'lucide-react';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';

const ACTION_LABELS = {
  suspend_org:           { label: 'Suspended Org',         color: 'var(--red)'    },
  activate_org:          { label: 'Activated Org',         color: 'var(--green)'  },
  update_org:            { label: 'Updated Org',           color: 'var(--blue)'   },
  impersonate_user:      { label: 'Impersonated User',     color: 'var(--amber)'  },
  send_reset_email:      { label: 'Sent Password Reset',   color: 'var(--blue)'   },
  send_email:            { label: 'Sent Email',            color: 'var(--blue)'   },
  update_user_email:     { label: 'Updated User Email',    color: 'var(--gold)'   },
  create_system_admin:   { label: 'Created System Admin',  color: 'var(--purple)' },
  activate_system_admin: { label: 'Activated Admin',       color: 'var(--green)'  },
  deactivate_system_admin:{ label: 'Deactivated Admin',    color: 'var(--red)'    },
};

export default function AuditLogPage() {
  const isMobile = useIsMobile();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [total,   setTotal]   = useState(0);
  const [filter,  setFilter]  = useState('');
  const [page,    setPage]    = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAuditLogs({ limit: LIMIT, offset: page * LIMIT, action: filter || undefined });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [filter]);

  const actionInfo = (action) => ACTION_LABELS[action] || { label: action.replace(/_/g, ' '), color: 'var(--text-muted)' };

  return (
    <Layout title="Audit Log" crumb={`${total} events`}>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <Filter size={13} style={{ color: 'var(--text-muted)' }} />
          <select
            className="form-select"
            style={{ width: 'auto', padding: '7px 12px' }}
            value={filter}
            onChange={e => setFilter(e.target.value)}
          >
            <option value="">All Actions</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /> Loading audit log…</div>
      ) : (
        <>
          {!isMobile ? (
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Action</th>
                    <th>Admin</th>
                    <th>Target</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const info = actionInfo(log.action);
                    return (
                      <tr key={log.id}>
                        <td className="mono-sm" style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(log.created_at)}
                        </td>
                        <td>
                          <span style={{
                            fontFamily: 'var(--font-mono)', fontSize: '0.65rem', fontWeight: 700,
                            color: info.color, textTransform: 'uppercase', letterSpacing: '0.05em',
                            background: `${info.color}18`, padding: '2px 8px', borderRadius: 4,
                            whiteSpace: 'nowrap',
                          }}>
                            {info.label}
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: '0.75rem' }}>{log.admin_email}</td>
                        <td>
                          <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{log.target_label || '—'}</div>
                          {log.target_id && (
                            <div className="uuid">{log.target_id}</div>
                          )}
                        </td>
                        <td className="mono-sm" style={{ maxWidth: 240 }}>
                          {log.metadata ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>
                              {Object.entries(log.metadata)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' · ')}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {logs.length === 0 && (
                    <tr><td colSpan={5}>
                      <div className="empty">
                        <Activity size={28} />
                        <h3>No audit events found</h3>
                        <p>Actions taken in the admin panel will appear here.</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          ) : (
            /* Mobile audit cards */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {logs.length === 0 ? (
                <div className="empty"><Activity size={28} /><h3>No audit events</h3><p>Actions in the admin panel appear here.</p></div>
              ) : logs.map(log => {
                const info = actionInfo(log.action);
                return (
                  <div key={log.id} className="card" style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: '0.6rem', fontWeight: 700,
                        color: info.color, textTransform: 'uppercase', letterSpacing: '0.05em',
                        background: `${info.color}18`, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                      }}>{info.label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)', flexShrink: 0 }}>{formatDate(log.created_at)}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 500, marginBottom: 2 }}>{log.target_label || '—'}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)' }}>{log.admin_email}</div>
                    {log.metadata && (
                      <div style={{ marginTop: 4, fontSize: '0.62rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {Object.entries(log.metadata).map(([k,v]) => `${k}: ${v}`).join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
              <span className="mono-sm">{page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <button className="btn btn-ghost btn-sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
